import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useState} from 'react';
import {BrowserRouter, MemoryRouter} from 'react-router-dom';

import {ReauthProvider} from '../auth/ReauthContext.js';
import {browserWebAuthnClient, type WebAuthnClient} from '../auth/webauthn.js';
import {WebAuthnProvider} from '../auth/WebAuthnContext.js';
import {FixturePanelDataSource} from '../data/FixturePanelDataSource.js';
import {HttpPanelDataSource} from '../data/HttpPanelDataSource.js';
import type {PanelDataSource} from '../data/PanelDataSource.js';
import {PanelProvider} from './PanelProvider.js';
import {PanelRouter} from './router.js';

export interface AppProps {
  /** Só em teste: liga o roteador de memória numa rota conhecida. */
  initialEntries?: string[];
  /** Trocado por adaptador HTTP nos planos 02, 04, 05 e 06. */
  dataSource?: PanelDataSource;
  /**
   * Só em teste: dublê da cerimônia WebAuthn, pelo mesmo caminho que o
   * `dataSource`. Em produção fica o navegador de verdade, e nenhum teste
   * depende de aparelho real.
   */
  webauthn?: WebAuthnClient;
}

const newQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {retry: false, refetchOnWindowFocus: false},
    },
  });

/**
 * Escolhe de onde vêm os dados.
 *
 * Com `VITE_PANEL_API_BASE` definida, o painel fala com a API. Sem ela, usa a
 * fixture, que é o modo de trabalho visual local e o que a suíte de comparação
 * de pixel exercita.
 *
 * A escolha é por configuração EXPLÍCITA, e não por tentativa: um painel que
 * tentasse a API e caísse na fixture ao falhar mostraria dado simulado
 * acreditando ser real, que é o pior desfecho possível numa tela de aprovação.
 * Se a API está configurada e cai, a tela mostra erro.
 *
 * **String vazia é ausência, não "mesma origem".** `''` é falsy, então
 * construir com `VITE_PANEL_API_BASE=""` esperando caminho relativo cai na
 * fixture, calado. O valor de produção é `/`: o `HttpPanelDataSource` corta a
 * barra final e monta `/v1/...`, que é relativo à origem que serviu a página, e
 * é o que o `panel.Caddyfile` encaminha para a API.
 *
 * A esteira de entrega já publicou um bundle construído sem a variável. O que
 * fecha isso hoje não é este comentário: é `infra/vps/entrega/
 * conferir-painel.sh`, que mede o bundle empacotado e o que o Caddy serve
 * depois de publicado, e reprova o painel que não tem as rotas da API.
 */
const escolherFonte = (): PanelDataSource => {
  const base = import.meta.env.VITE_PANEL_API_BASE;
  return base
    ? new HttpPanelDataSource({baseUrl: base})
    : new FixturePanelDataSource();
};

export const App = ({initialEntries, dataSource, webauthn}: AppProps) => {
  const [client] = useState(newQueryClient);
  const [source] = useState<PanelDataSource>(
    () => dataSource ?? escolherFonte(),
  );

  // Reauth por fora do PanelProvider, porque o provider oferece a cerimônia
  // quando uma ação volta recusada; WebAuthn por fora dos dois, porque tanto a
  // tela de entrada quanto o diálogo usam o mesmo cliente.
  const tree = (
    <WebAuthnProvider client={webauthn ?? browserWebAuthnClient}>
      <ReauthProvider dataSource={source}>
        <PanelProvider dataSource={source}>
          <PanelRouter />
        </PanelProvider>
      </ReauthProvider>
    </WebAuthnProvider>
  );

  return (
    <QueryClientProvider client={client}>
      {initialEntries ? (
        <MemoryRouter initialEntries={initialEntries}>{tree}</MemoryRouter>
      ) : (
        <BrowserRouter>{tree}</BrowserRouter>
      )}
    </QueryClientProvider>
  );
};
